import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  apiErrorMessage,
  useAddUserNote,
  useAdminUser,
  useBanUser,
  useUnbanUser,
} from '../lib/api'
import { useAdminAuth } from '../lib/auth'
import { fmtDateTime, fmtEur } from '../lib/format'

const SEV_TONE: Record<string, string> = {
  critical: 'admin-pill admin-pill--danger',
  high:     'admin-pill admin-pill--danger',
  medium:   'admin-pill admin-pill--warning',
  low:      'admin-pill admin-pill--info',
}

function SeverityPill({ severity }: { severity: 'low' | 'medium' | 'high' | 'critical' }) {
  return <span className={SEV_TONE[severity] ?? 'admin-pill'}>{severity}</span>
}

const CARD: React.CSSProperties = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-5)',
}

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>()
  const q = useAdminUser(id)
  const { user: meUser } = useAdminAuth()
  const ban = useBanUser()
  const unban = useUnbanUser()
  const addNote = useAddUserNote()
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')

  const canWrite = meUser?.role === 'admin' || meUser?.role === 'super_admin'

  if (!id) return null
  if (q.isLoading) return <div style={{ padding: 'var(--space-4)', color: 'var(--text-secondary)' }}>Loading…</div>
  if (q.isError || !q.data) {
    return <div className="auth-card__error" style={{ display: 'flex' }}>{apiErrorMessage(q.error)}</div>
  }

  const d = q.data
  const banned = !!d.user.bannedAt

  return (
    <>
      <header className="page-header">
        <div className="page-header__title-row">
          <div>
            <Link to="/users" style={{ fontSize: 'var(--text-sm)', color: 'var(--admin-accent-text)' }}>← Back to users</Link>
            <h1 className="page-header__title" style={{ marginTop: 'var(--space-1)' }}>{d.user.displayName ?? '—'}</h1>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: 0 }}>{d.user.email}</p>
          </div>
        </div>
        <div className="page-header__actions">
          {banned
            ? <span className="admin-pill admin-pill--danger">BANNED</span>
            : <span className="admin-pill admin-pill--success">active</span>}
          <span className="admin-pill admin-pill--info" style={{ marginLeft: 8 }}>{d.user.role}</span>
        </div>
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-5)', marginBottom: 'var(--space-5)' }}>
        <section style={CARD}>
          <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)' }}>Profile</h3>
          <dl style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 'var(--space-2)', columnGap: 'var(--space-3)', fontSize: 'var(--text-sm)', margin: 0 }}>
            <dt style={{ color: 'var(--text-secondary)' }}>User ID</dt>
            <dd style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{d.user.id}</dd>
            <dt style={{ color: 'var(--text-secondary)' }}>Country</dt>
            <dd>{d.user.countryCode ?? '—'}</dd>
            <dt style={{ color: 'var(--text-secondary)' }}>Email verified</dt>
            <dd>{d.user.emailVerifiedAt ? fmtDateTime(d.user.emailVerifiedAt) : '—'}</dd>
            <dt style={{ color: 'var(--text-secondary)' }}>Created</dt>
            <dd>{fmtDateTime(d.user.createdAt)}</dd>
            {banned && (<>
              <dt style={{ color: 'var(--text-secondary)' }}>Ban reason</dt>
              <dd style={{ color: 'var(--admin-accent-text)' }}>{d.user.banReason ?? '—'}</dd>
            </>)}
          </dl>
        </section>

        <section style={CARD}>
          <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)' }}>Active account</h3>
          {!d.account && <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>No active account.</p>}
          {d.account && (
            <dl style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 'var(--space-2)', columnGap: 'var(--space-3)', fontSize: 'var(--text-sm)', margin: 0 }}>
              <dt style={{ color: 'var(--text-secondary)' }}>Tier</dt>
              <dd style={{ fontFamily: 'var(--font-mono)' }}>{d.account.tierCode}</dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Phase</dt>
              <dd><span className="admin-pill admin-pill--info">{d.account.currentPhase}</span></dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Starting balance</dt>
              <dd>{fmtEur(d.account.startingBalance)}</dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Current balance</dt>
              <dd>{fmtEur(d.account.currentBalance)}</dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Started</dt>
              <dd>{fmtDateTime(d.account.startedAt)}</dd>
            </dl>
          )}
        </section>
      </div>

      {canWrite && (
        <section style={{ ...CARD, marginBottom: 'var(--space-5)' }}>
          <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)' }}>Actions</h3>
          {!banned ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 'var(--space-3)' }}>
              <div className="field" style={{ flex: '1 1 320px', maxWidth: 480 }}>
                <label className="field__label" htmlFor="ban-reason">Ban reason</label>
                <input id="ban-reason" type="text" className="field__input" value={reason}
                  onChange={(e) => setReason(e.target.value)} placeholder="Min. 3 characters." />
              </div>
              <button type="button" className="btn btn--danger" disabled={ban.isPending || reason.trim().length < 3}
                onClick={() => ban.mutate({ id, reason }, { onSuccess: () => setReason('') })}>
                {ban.isPending ? 'Banning…' : 'Ban user'}
              </button>
              {ban.isError && <div className="auth-card__error" style={{ display: 'flex' }}>{apiErrorMessage(ban.error)}</div>}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <button type="button" className="btn btn--primary" disabled={unban.isPending} onClick={() => unban.mutate({ id })}>
                {unban.isPending ? 'Unbanning…' : 'Lift ban'}
              </button>
              {unban.isError && <div className="auth-card__error" style={{ display: 'flex' }}>{apiErrorMessage(unban.error)}</div>}
            </div>
          )}
        </section>
      )}

      {canWrite && (
        <section style={{ ...CARD, marginBottom: 'var(--space-5)' }}>
          <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)' }}>Add note</h3>
          <textarea className="field__input" style={{ minHeight: 80 }} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note about this user…" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginTop: 'var(--space-3)' }}>
            <button type="button" className="btn btn--primary" disabled={addNote.isPending || note.trim().length < 1}
              onClick={() => addNote.mutate({ id, note }, { onSuccess: () => setNote('') })}>
              {addNote.isPending ? 'Saving…' : 'Add note'}
            </button>
            {addNote.isError && <div className="auth-card__error" style={{ display: 'flex' }}>{apiErrorMessage(addNote.error)}</div>}
          </div>
        </section>
      )}

      {d.notes.length > 0 && (
        <section style={{ ...CARD, marginBottom: 'var(--space-5)' }}>
          <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)' }}>Notes</h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {d.notes.map((n) => (
              <li key={n.id} style={{ background: 'var(--bg-subtle)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-3)', fontSize: 'var(--text-sm)' }}>
                <div>{n.note}</div>
                <div style={{ marginTop: 'var(--space-1)', fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
                  {fmtDateTime(n.createdAt)} · actor {n.actorId}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section style={CARD}>
        <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)' }}>Recent fraud signals</h3>
        {d.recentFraudSignals.length === 0 && (
          <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>No signals.</p>
        )}
        {d.recentFraudSignals.length > 0 && (
          <div className="admin-table">
            <table>
              <thead>
                <tr><th>Type</th><th>Severity</th><th>Resolved</th><th>Created</th></tr>
              </thead>
              <tbody>
                {d.recentFraudSignals.map((s) => (
                  <tr key={s.id}>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{s.signalType}</td>
                    <td><SeverityPill severity={s.severity} /></td>
                    <td>{s.resolved ? '✓' : '—'}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{fmtDateTime(s.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}
